import apiClient from './apiClient';
import type { DialpadEntityBucket } from './organizationService';

// TypeScript interfaces for paginated response
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// TypeScript interfaces generated from actual API specification
export interface JobGroup {
  id: number;
  uuid: string;
  name: string; // Job group name (primary field)
  description?: string;
  created_at: string;
  updated_at: string;
  created_by?: AppUser;
  created_by_email?: string; // Email of creator
  group?: UserGroup;
  group_name?: string; // User group name (optional, for reference only)
  metadata: Record<string, any>;
  job_count: number;
  jobs: JobGroupJob[];
  status?: string; // Optional status property
}

export interface JobGroupJob {
  id: number;
  uuid: string;
  name: string;
  execution_order: number;
  job_type?: {
    id: number;
    name: string;
    code: string;
  };
}

export interface AppUser {
  uuid: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_staff: boolean;
  date_joined: string;
  group?: UserGroup;
}

export interface UserGroup {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  is_default: boolean;
}

export interface CreateJobGroupRequest {
  name: string;
  description?: string;
  metadata?: Record<string, any>;
}

export interface CreateJobGroupResponse {
  message: string;
  job_group: JobGroup;
}

export interface UpdateJobGroupRequest extends Partial<CreateJobGroupRequest> {}

export interface QueueJobsRequest {
  source_platform: number;
  target_platform: number;
  job_group: number;
  force_refresh?: boolean;
  selected_job_types?: number[];
}

export interface QueueJobsResponse {
  message: string;
  template: {
    source_platform: string;
    target_platform: string;
    job_type_ids: number[];
  };
  dialpad_guardrails?: DialpadGuardrailPreview | null;
  created_jobs: QueuedJob[];
}

export interface CancelJobGroupResponse {
  success: boolean;
  message: string;
  signal_id?: number;
  job_group_id: number;
  error?: string;
}

export interface QueuedJob {
  id: number;
  name: string; // Add name to match what the UI expects
  job_type_id: number;
  job_type_name: string;
  source_platform_name: string; // Add source platform name
  target_platform_name: string; // Add target platform name
  execution_order: number;
  status: string;
  job_type?: {
    id: number;
    name: string;
    code?: string;
  };
}

export interface PreviewTemplateRequest {
  source_platform: number;
  target_platform: number;
}

export interface JobTypePreview {
  id: number;
  name: string;
  code: string;
}

export interface PreviewTemplateResponse {
  template_found: boolean;
  template?: {
    source_platform: string;
    target_platform: string;
    job_type_ids: number[];
  };
  job_types: JobTypePreview[];
  error?: string;
  credential_warnings?: Array<{
    platform: string;
    platform_id: number;
    type: 'source' | 'target';
    message: string;
  }>;
  has_all_credentials?: boolean;
  dialpad_guardrails?: DialpadGuardrailPreview | null;
}

export interface DialpadGuardrailPreview {
  platform_type: string;
  direction: 'source' | 'target';
  hard_limit_enabled: boolean;
  entity_buckets: DialpadEntityBucket[];
  entity_caps: Partial<Record<DialpadEntityBucket, number>>;
  missing_caps: DialpadEntityBucket[];
  requires_default_office: boolean;
  default_office_configured: boolean;
  default_office_id: string;
  queue_allowed: boolean;
  error?: string;
}

export interface JobGroupPreview {
  job_group: {
    id: number;
    name: string;
    description: string;
    created_at: string;
    job_count: number;
  };
  jobs: JobPreview[];
  credential_status: {
    source_platforms: Record<string, CredentialStatus>;
    target_platforms: Record<string, CredentialStatus>;
  };
}

export interface JobPreview {
  id: number;
  job_type: {
    id: number;
    name: string;
    code: string;
  };
  source_platform?: {
    id: number;
    name: string;
    platform_type: string;
    requires_browser_automation: boolean;
  };
  target_platform?: {
    id: number;
    name: string;
    platform_type: string;
    requires_browser_automation: boolean;
  };
  execution_order: number;
  status: string;
}

export interface CredentialStatus {
  type: 'organization' | 'user_browser' | null;
  exists: boolean;
  stored_at?: string;
  ttl_seconds?: number;
}

class JobGroupService {
  async getAllJobGroups(): Promise<JobGroup[]> {
    const response = await apiClient.get<PaginatedResponse<JobGroup>>('/api/job-groups/');
    // Extract results from paginated response - API already returns name as job group name
    return response.data.results;
  }

  async getJobGroup(uuid: string): Promise<JobGroup> {
    const response = await apiClient.get(`/api/job-groups/${uuid}/`);
    return response.data;
  }

  async createJobGroup(data: CreateJobGroupRequest): Promise<CreateJobGroupResponse> {
    const response = await apiClient.post('/api/job-groups/', data);
    return response.data;
  }

  async updateJobGroup(uuid: string, data: UpdateJobGroupRequest): Promise<JobGroup> {
    const response = await apiClient.put(`/api/job-groups/${uuid}/`, data);
    return response.data;
  }

  async deleteJobGroup(uuid: string): Promise<void> {
    await apiClient.delete(`/api/job-groups/${uuid}/`);
  }

  async addJobToGroup(groupUuid: string, jobId: number, executionOrder?: number): Promise<void> {
    await apiClient.post(`/api/job-groups/${groupUuid}/jobs/`, {
      job_id: jobId,
      execution_order: executionOrder
    });
  }

  async addJobTypeToGroup(groupId: number, jobTypeId: number): Promise<any> {
    const response = await apiClient.post(`/api/job-groups/${groupId}/add-job/`, {
      job_type_id: jobTypeId
    });
    return response.data;
  }

  async removeJobFromGroup(groupUuid: string, jobId: number): Promise<void> {
    await apiClient.delete(`/api/job-groups/${groupUuid}/jobs/${jobId}/`);
  }

  async updateJobOrder(groupUuid: string, jobId: number, executionOrder: number): Promise<void> {
    await apiClient.patch(`/api/job-groups/${groupUuid}/jobs/${jobId}/`, {
      execution_order: executionOrder
    });
  }

  async executeJobGroup(groupUuid: string): Promise<void> {
    await apiClient.post(`/api/job-groups/${groupUuid}/execute/`);
  }

  async queueJobsFromTemplate(data: QueueJobsRequest): Promise<QueueJobsResponse> {
    const response = await apiClient.post('/api/job-groups/queue-jobs/', data);
    return response.data;
  }

  async cancelJobGroup(jobGroupId: number): Promise<CancelJobGroupResponse> {
    const response = await apiClient.post(`/api/cancel-job-group/${jobGroupId}/`);
    return response.data;
  }

  async previewJobTemplate(data: PreviewTemplateRequest): Promise<PreviewTemplateResponse> {
    const response = await apiClient.post('/api/job-groups/preview-template/', data);
    return response.data;
  }

  async previewJobGroup(jobGroupId: number): Promise<JobGroupPreview> {
    const response = await apiClient.get(`/api/job-groups/${jobGroupId}/preview/`);
    return response.data;
  }
}

export const jobGroupService = new JobGroupService();
